import * as React from "react";
import AuthGate from "./AuthGate";
import Mainlayout from "../components/layouts/MainLayout";
import References from "../features/references/components/References";
import DraftMode from "./DraftMode";
import AppModeToggle, { AppMode } from "./AppModeToggle";
import { useHostAdapter } from "../platform";
import { warmCheckOutIdentity } from "../platform/shared/identityOnce";
import { JyloDocumentDetector } from "../platform/word/assembly";
import CheckOutStatus from "../features/checkout/components/CheckOutStatus";

// Check out is NOT a mode the user switches into — it is something they do to a
// document they already have open, so it has no side-nav entry. Save and Check in live
// in the pane header (see CheckOutActions, rendered by MainLayout) and raise an overlay
// over whatever mode is showing; the draft and its chat stay mounted underneath.
//
// Which modes exist at all depends on the host. Word has Draft and Assembly; Excel and
// PowerPoint have Draft only, so their mode toggle hides itself entirely.

const App = () => {
  const host = useHostAdapter();
  const assistant = host.assistant;
  // Draft is the landing mode wherever the assistant reaches; otherwise there is no
  // mode to land in and the check-out status is the pane's whole content.
  const [mode, setMode] = React.useState<AppMode>(assistant ? "draft" : "checkout");
  const [isAssembly, setIsAssembly] = React.useState(false);
  // Read the document's identity now, while the document has just opened and nothing is
  // being edited. Excel refuses the Rich API mid-cell-edit and a ribbon press does not
  // clear edit mode, so asking at press time was the one moment it could fail.
  React.useEffect(() => {
    if (!host.documents) return;
    warmCheckOutIdentity(host.documents);
  }, [host]);

  // Assembly documents are a Word-only concept: the reference placeholders are Word
  // content controls, and no other host has the primitive. Gate on the capability
  // rather than the host name, so the answer lives in one place.
  React.useEffect(() => {
    if (!host.capabilities.assembly) return;
    JyloDocumentDetector.isJyloDocument().then((result) => {
      if (result.isJylo && result.assemblyObjectIdentifier) {
        setIsAssembly(true);
        setMode("references");
      }
    });
  }, [host]);

  const modeToggle = (
    <AppModeToggle
      mode={mode}
      onChange={setMode}
      isAssembly={isAssembly}
      assemblySupported={host.capabilities.assembly}
      draftSupported={assistant !== undefined}
    />
  );

  // CheckOutStatus is the fallback for a host with no assistant and no assembly -- it
  // exists so such a pane says something useful rather than nothing. No host is in that
  // position any more: Excel and PowerPoint both have assistants, so this is dead.
  //
  // Once every host has an assistant this branch becomes unreachable and the view needs
  // a decision: promote it to a mode of its own (it is genuinely useful -- it names the
  // holder and the last save, which the ribbon overlay only shows on a press) or delete
  // it. Deliberately not decided here, because adding a third entry to Word's mode menu
  // is a change to the shipping product and not this phase's business.
  const body =
    mode === "references" && host.capabilities.assembly ? (
      <References modeBar={modeToggle} />
    ) : assistant && mode === "draft" ? (
      <DraftMode assistant={assistant} modeBar={modeToggle} />
    ) : (
      <Mainlayout modeBar={modeToggle}>
        {host.documents ? <CheckOutStatus documents={host.documents} /> : null}
      </Mainlayout>
    );

  return <AuthGate>{body}</AuthGate>;
};

export default App;
